import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Heart, Loader2, XCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const POLL_MAX = 15;
const POLL_INTERVAL = 2000;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  // checking | paid | expired | pending | failed | notfound
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setState("notfound");
      return;
    }
    let attempts = 0;
    let stopped = false;

    const poll = async () => {
      if (stopped) return;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        setDetail(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (data.status === "expired") {
          setState("expired");
          return;
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setState("pending");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (e?.response?.status === 404) {
          setState("notfound");
          return;
        }
        attempts += 1;
        if (attempts >= POLL_MAX) {
          setState("failed");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  return (
    <div
      data-testid="donate-success-page"
      className="min-h-screen flex items-center justify-center bg-brand-cream px-4 py-20"
    >
      <div className="w-full max-w-xl bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-200 text-center">
        {state === "checking" && (
          <>
            <Loader2 className="w-14 h-14 text-brand-coral animate-spin mx-auto" />
            <h1 className="mt-6 font-display font-black text-3xl text-brand-navy">
              Confirming your donation...
            </h1>
            <p className="mt-3 text-slate-600">
              Hang tight — we&apos;re verifying the payment with Stripe.
            </p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="mt-6 font-display font-black text-4xl text-brand-navy">
              Thank you!
            </h1>
            <p className="mt-3 text-lg text-slate-600">
              Your donation of{" "}
              <span className="font-bold text-brand-coral">
                ${detail?.amount_total?.toFixed(2)}
              </span>{" "}
              just helped a kid walk through the door.
            </p>
            <div className="mt-8 p-5 bg-brand-sand rounded-2xl text-left">
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-brand-coral fill-brand-coral flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700 leading-relaxed">
                  A tax-deductible receipt will be emailed to you shortly.
                  We&apos;ll also update you with impact stories as your
                  support lands.
                </div>
              </div>
            </div>
            <Link to="/">
              <Button
                data-testid="success-home-btn"
                className="mt-8 rounded-full bg-brand-navy hover:bg-slate-800 text-white font-bold px-8 h-12"
              >
                Back home
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </>
        )}

        {state === "expired" && (
          <>
            <XCircle className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="mt-6 font-display font-black text-3xl text-brand-navy">
              That session expired
            </h1>
            <p className="mt-3 text-slate-600">
              No charge was made. You can try again whenever you&apos;re ready.
            </p>
            <Button
              onClick={() => navigate("/#donate")}
              data-testid="success-retry-btn"
              className="mt-6 rounded-full bg-brand-coral hover:bg-brand-coralDark text-white font-bold px-8 h-12"
            >
              Try again
            </Button>
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 className="w-14 h-14 text-amber-500 mx-auto" />
            <h1 className="mt-6 font-display font-black text-3xl text-brand-navy">
              Payment received — still finalizing
            </h1>
            <p className="mt-3 text-slate-600">
              Your payment is going through. Stripe will email you a receipt
              shortly. You can safely close this page.
            </p>
            <Link to="/">
              <Button
                data-testid="pending-home-btn"
                className="mt-6 rounded-full bg-brand-navy hover:bg-slate-800 text-white font-bold px-8 h-12"
              >
                Back home
              </Button>
            </Link>
          </>
        )}

        {state === "failed" && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto" />
            <h1 className="mt-6 font-display font-black text-3xl text-brand-navy">
              We couldn&apos;t confirm your donation yet
            </h1>
            <p className="mt-3 text-slate-600">
              If a charge was made, check your email for a Stripe receipt.
              Contact us if this persists.
            </p>
            <Link to="/">
              <Button
                data-testid="failed-home-btn"
                className="mt-6 rounded-full bg-brand-navy hover:bg-slate-800 text-white font-bold px-8 h-12"
              >
                Back home
              </Button>
            </Link>
          </>
        )}

        {state === "notfound" && (
          <>
            <XCircle className="w-14 h-14 text-slate-400 mx-auto" />
            <h1 className="mt-6 font-display font-black text-3xl text-brand-navy">
              No donation session found
            </h1>
            <p className="mt-3 text-slate-600">
              Looks like you landed here directly. Head back to the homepage
              to start a donation.
            </p>
            <Link to="/">
              <Button
                data-testid="notfound-home-btn"
                className="mt-6 rounded-full bg-brand-navy hover:bg-slate-800 text-white font-bold px-8 h-12"
              >
                Back home
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
